import {
  CategoryInfo,
  FinancialItems,
  financialAssetOptions,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import { getFinancialItemType } from 'src/financial/financial-data/tables/FinancialItemGenericTable';

export type FinancialRow = {
  id: string;
  memberName: string;
  product: string;
  navProduct: string;
  description?: string;
  provider?: string;
  typeLabel?: string;
  balanceDate?: string;
  balance: number | null;
  isUpdatedAutomatically?: boolean;
  hasFeedData?: boolean;
};

export type FinancialSubSection = {
  key: string;
  title: string;
  rows: FinancialRow[];
  total: number;
};

type FinancialCategoryKey = 'spendingAccounts' | 'assets' | 'liabilities' | 'protections';

export type FinancialCategory = {
  key: FinancialCategoryKey;
  title: string;
  rows: FinancialRow[];
  total: number;
  totalLabel?: string;
  subSections?: FinancialSubSection[];
};

type GroupedRows = {
  spendingAccounts: FinancialRow[];
  assets: FinancialRow[];
  liabilities: FinancialRow[];
  protections: FinancialRow[];
};

export const emptyGroupedRows = (): GroupedRows => ({
  spendingAccounts: [],
  assets: [],
  liabilities: [],
  protections: [],
});

const sumBalances = (rows: FinancialRow[]) =>
  rows.reduce((acc, row) => acc + (row.balance ?? 0), 0);

export function extractRows(items: FinancialItems, memberName: string): GroupedRows {
  const { metadata } = items;

  return {
    spendingAccounts: items.spendingAccounts.map(item => ({
      id: item.id,
      memberName,
      product: item.product,
      navProduct: item.product,
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      balanceDate: item.balanceDate,
      balance: item.currentBalance,
    })),
    assets: items.assets.map(item => ({
      id: item.id,
      memberName,
      product: item.product,
      navProduct: 'pensionProduct' in item ? item.pensionProduct : item.product,
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      balanceDate: item.valueDate,
      balance: item.value,
      isUpdatedAutomatically: item.isUpdatedAutomatically,
      hasFeedData: item.hasFeedData,
    })),
    liabilities: items.liabilities.map(item => ({
      id: item.id,
      memberName,
      product: item.product,
      navProduct: item.product,
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      balanceDate: item.remainingBalanceDate,
      balance: item.remainingBalance,
    })),
    protections: items.protections.map(item => ({
      id: item.id,
      memberName,
      product: item.product,
      navProduct: item.product,
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      balanceDate: undefined,
      balance: item.coverAmount ?? null,
    })),
  };
}

export function mergeGroupedRows(groups: GroupedRows[]): GroupedRows {
  return {
    spendingAccounts: groups.flatMap(group => group.spendingAccounts),
    assets: groups.flatMap(group => group.assets),
    liabilities: groups.flatMap(group => group.liabilities),
    protections: groups.flatMap(group => group.protections),
  };
}

function buildSubSections(
  category: 'ASSET' | 'LIABILITY',
  rows: FinancialRow[],
): FinancialSubSection[] {
  const categoryInfo: CategoryInfo = financialAssetOptions[category];
  const knownProducts = new Set<string>(categoryInfo.productsOrder);
  const sections = categoryInfo.productsOrder
    .map(product => {
      const productRows = rows.filter(row => row.product === product);
      return {
        key: product,
        title: categoryInfo.products[product].label,
        rows: productRows,
        total: sumBalances(productRows),
      };
    })
    .filter(section => section.rows.length > 0);

  // Capture any rows whose product isn't in productsOrder so the displayed
  // sub-sections always reconcile with the section total.
  const otherRows = rows.filter(row => !knownProducts.has(row.product));
  if (otherRows.length > 0) {
    sections.push({ key: 'OTHER', title: 'Other', rows: otherRows, total: sumBalances(otherRows) });
  }

  return sections;
}

export function buildCategories(grouped: GroupedRows): FinancialCategory[] {
  return [
    {
      key: 'spendingAccounts',
      title: 'Spending accounts',
      rows: grouped.spendingAccounts,
      total: sumBalances(grouped.spendingAccounts),
    },
    {
      key: 'assets',
      title: 'Assets',
      rows: grouped.assets,
      total: sumBalances(grouped.assets),
      subSections: buildSubSections('ASSET', grouped.assets),
    },
    {
      key: 'liabilities',
      title: 'Liabilities',
      rows: grouped.liabilities,
      total: sumBalances(grouped.liabilities),
      subSections: buildSubSections('LIABILITY', grouped.liabilities),
    },
    {
      key: 'protections',
      title: 'Protection policies',
      rows: grouped.protections,
      total: sumBalances(grouped.protections),
      totalLabel: 'Total cover',
    },
  ];
}
